import React, { useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const PostLaminationEL = ({ formData, onFormDataChange }) => {
    // Destructure formData with default values
    const { postLaminationObservations = [], nonConformanceObservations = '' } = formData;

    useEffect(() => {
        // Check if formData already has prefilled data
        const hasFormData =
            postLaminationObservations.length > 0 || nonConformanceObservations.trim() !== '';

        if (!hasFormData) {
            // Step 1: Fetch metadata from sessionStorage
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));

            let prefilledPostLaminationData = [];
            let prefilledObservations = [];
            let prefilledNonConformance = '';

            // Step 2: Check metadata for prefilled data
            if (
                metadata &&
                metadata.post &&
                metadata.post.PostLaminationEL_post
            ) {
                prefilledPostLaminationData = metadata.post.PostLaminationEL_post || [];
                prefilledObservations = prefilledPostLaminationData.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: '',
                    observation2: '',
                }));
            }

            // Step 3: Fetch inspectionData from sessionStorage
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

            // Step 4: Check inspectionData for observations and non-conformance data
            if (
                inspectionData &&
                inspectionData.PostLamination &&
                inspectionData.PostLamination.PostLaminationEL_post
            ) {
                const { PostLaminationEL, nonConformance: inspectionNonConformance } =
                    inspectionData.PostLamination.PostLaminationEL_post;

                prefilledPostLaminationData = PostLaminationEL || [];
                prefilledObservations = PostLaminationEL.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));

                prefilledNonConformance = inspectionNonConformance || '';
            }

            // Step 5: Update formData only if prefilled data exists
            if (
                prefilledPostLaminationData.length > 0 ||
                prefilledObservations.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    postLaminationObservations: prefilledObservations,
                    nonConformanceObservations: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);

    // Handle observation input change for the table (both Observation 1 and 2)
    const handleObservationChange = (index, field, value) => {
        const updatedObservations = [...postLaminationObservations];
        updatedObservations[index][field] = value;
        onFormDataChange({ ...formData, postLaminationObservations: updatedObservations });
    };

    // Handle non-conformance input change
    const handleNonConformanceChange = (value) => {
        onFormDataChange({ ...formData, nonConformanceObservations: value });
    };

    // Function to send observations to the API
    const submitObservations = async () => {
        const incompleteObservation = postLaminationObservations.some(obs => !obs.observation1.trim());
        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }

        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

            const combinedData = postLaminationObservations.map((item) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            }));

            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PostLamination: {
                    PostLaminationEL_post: {
                        PostLaminationEL: combinedData,
                        nonConformance: nonConformanceObservations || '',
                    },
                },
            };

            await submitFormData(payload);
            alert('Observations submitted successfully!');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations.');
        }
    };

    // Render the post-lamination EL table
    const renderPostLaminationELTable = () => (
        <div className="mt-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-700">Post-Lamination EL</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(postLaminationObservations || []).map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={item.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={item.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Post-Lamination EL Inspection</h3>

            {renderPostLaminationELTable()}

            {/* Non-Conformance Section */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-700">Non-Conformance</h4>
                <textarea
                    value={nonConformanceObservations || ''}
                    onChange={(e) => handleNonConformanceChange(e.target.value)}
                    placeholder="Enter non-conformance observations"
                    rows="4"
                    className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                />
            </div>

            {/* Submit Observations Button */}
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500 transition-colors duration-150"
            >
                Submit
            </button>
        </div>
    );
};

export default PostLaminationEL;